import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useReactMediaRecorder } from 'react-media-recorder';
import {
  DEFAULT_VIDEO_MIME_TYPES,
  createMediaUploader,
} from '../../utils/mediaUpload';
import { getHitLabApiMessage } from './hitLabUtils';
import {
  VOICE_DROP_MAX_SECONDS,
  VOICE_DROP_MIN_SECONDS,
  VOICE_DROP_UPLOAD_PREFIX,
  getMyVoiceDrop,
  normalizeVoiceDropPayload,
  submitVoiceDrop,
} from './hitLabRadioVoiceDropApi';

const AUDIO_MIME_TYPES = new Set([
  ...DEFAULT_VIDEO_MIME_TYPES,
  'audio/aac',
  'audio/mp4',
  'audio/mpeg',
  'audio/mpeg-3',
  'audio/mp3',
  'audio/ogg',
  'audio/wav',
  'audio/webm',
  'video/webm',
]);

const isAudioUploadName = (name = '') =>
  /\.(aac|m4a|mp3|oga|ogg|wav|webm)$/i.test(String(name || ''));

const formatSeconds = (value) => {
  const seconds = Math.max(0, Math.round(Number(value || 0)));
  return `${seconds}s`;
};

const APPROVED_LIVE_LABEL = 'Approved \u2014 live in JG Radio rotation';

const getStatusLabel = (status) => {
  if (status === 'submitted') return 'Submitted for review';
  if (status === 'approved') return 'Approved';
  if (status === 'rejected') return 'Not approved';
  return String(status || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const getAudioFileExtension = (mimeType = '') => {
  if (/ogg/i.test(mimeType)) return 'ogg';
  if (/wav/i.test(mimeType)) return 'wav';
  if (/mp4|aac/i.test(mimeType)) return 'm4a';
  if (/mpeg|mp3/i.test(mimeType)) return 'mp3';
  return 'webm';
};

export default function HitLabRadioListenerVoiceDropPrompt({
  player,
  pass,
  refreshKey = 0,
}) {
  const isEligiblePass = ['submitted', 'approved'].includes(pass?.status);
  const previewAudioRef = useRef(null);
  const stopTimerRef = useRef(null);
  const [voiceDrop, setVoiceDrop] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [durationSeconds, setDurationSeconds] = useState(0);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [listenerScript, setListenerScript] = useState('');
  const [permissionGranted, setPermissionGranted] = useState(false);

  const {
    status,
    startRecording,
    stopRecording,
    mediaBlobUrl,
    clearBlobUrl,
  } = useReactMediaRecorder({ audio: true });

  const isRecording = status === 'recording';
  const uploader = useMemo(
    () =>
      createMediaUploader({
        videoMimeTypes: AUDIO_MIME_TYPES,
        maxVideoBytes: 12 * 1024 * 1024,
        maxVideoDurationSec: VOICE_DROP_MAX_SECONDS,
        enableThumbnails: false,
        enableAdaptiveLadder: false,
        isVideoName: isAudioUploadName,
      }),
    [],
  );

  const loadVoiceDrop = useCallback(async () => {
    if (!player?.token || !isEligiblePass) return;
    setLoading(true);
    setError('');
    try {
      const payload = normalizeVoiceDropPayload(await getMyVoiceDrop({ player }));
      setVoiceDrop(payload.voiceDrop);
    } catch (err) {
      setError(getHitLabApiMessage(err, 'Voice drop status could not be loaded.'));
    } finally {
      setLoading(false);
    }
  }, [isEligiblePass, player]);

  useEffect(() => {
    void loadVoiceDrop();
  }, [loadVoiceDrop, refreshKey]);

  useEffect(
    () => () => {
      if (stopTimerRef.current) window.clearTimeout(stopTimerRef.current);
      clearBlobUrl?.();
    },
    [clearBlobUrl],
  );

  const startVoiceDropRecording = () => {
    setError('');
    setMessage('');
    setDurationSeconds(0);
    clearBlobUrl?.();
    startRecording();
    if (stopTimerRef.current) window.clearTimeout(stopTimerRef.current);
    stopTimerRef.current = window.setTimeout(() => {
      stopRecording();
    }, VOICE_DROP_MAX_SECONDS * 1000);
  };

  const stopVoiceDropRecording = () => {
    if (stopTimerRef.current) window.clearTimeout(stopTimerRef.current);
    stopTimerRef.current = null;
    stopRecording();
  };

  const clearRecording = () => {
    setDurationSeconds(0);
    clearBlobUrl?.();
  };

  const handleLoadedMetadata = () => {
    const duration = Number(previewAudioRef.current?.duration || 0);
    if (Number.isFinite(duration)) setDurationSeconds(Math.round(duration));
  };

  const submitRecording = async () => {
    if (!mediaBlobUrl || busy) return;
    if (!permissionGranted) {
      setError('Voice drop consent is required.');
      return;
    }
    const duration = Math.round(durationSeconds);
    if (duration < VOICE_DROP_MIN_SECONDS || duration > VOICE_DROP_MAX_SECONDS) {
      setError('Voice drops must be 10-20 seconds.');
      return;
    }
    setBusy(true);
    setError('');
    setMessage('');
    setUploadProgress(0);
    try {
      const audioBlob = await fetch(mediaBlobUrl).then((response) => response.blob());
      const mimeType = audioBlob.type || 'audio/webm';
      const extension = getAudioFileExtension(mimeType);
      const audioFile = new File(
        [audioBlob],
        `jg-radio-listener-voice-drop.${extension}`,
        { type: mimeType },
      );
      uploader.validate(audioFile);
      const result = await uploader.upload(audioFile, {
        publicIdPrefix: VOICE_DROP_UPLOAD_PREFIX,
        transform: false,
        returnRaw: true,
        dedupe: false,
        onProgress: setUploadProgress,
      });
      const payload = normalizeVoiceDropPayload(
        await submitVoiceDrop({
          player,
          voiceDrop: {
            audioUrl: result.secureUrl,
            audioStorageKey: result.raw?.public_id || '',
            audioMimeType: mimeType,
            durationSeconds: duration,
            fileSizeBytes: audioFile.size,
            consentGranted: true,
            listenerScript,
          },
        }),
      );
      setVoiceDrop(payload.voiceDrop);
      setMessage('Submitted for review');
      setPermissionGranted(false);
      clearRecording();
    } catch (err) {
      setError(getHitLabApiMessage(err, 'Voice drop could not be submitted.'));
    } finally {
      setBusy(false);
    }
  };

  if (!isEligiblePass) return null;

  const canRecordNew = !voiceDrop || voiceDrop.status === 'rejected';
  const hasValidDuration =
    durationSeconds >= VOICE_DROP_MIN_SECONDS &&
    durationSeconds <= VOICE_DROP_MAX_SECONDS;

  return (
    <section className="hitlab-radio-voice-drop" aria-label="Listener voice drop">
      <div className="hitlab-radio-voice-drop-header">
        <div>
          <p className="hitlab-kicker">OPTIONAL VOICE DROP</p>
          <h3>Go live on JG Radio</h3>
        </div>
        {voiceDrop ? (
          <span className="hitlab-radio-voice-drop-status">
            {getStatusLabel(voiceDrop.status)}
          </span>
        ) : null}
      </div>

      <p>
        Record a 10&ndash;20 second listener drop. If approved, your voice gets
        added to JG Radio live rotation.
      </p>
      <p>
        Your written feedback pass is already submitted. This optional voice drop
        is reviewed separately and does not affect your 5,000 JGK reward decision.
      </p>
      <p>Say something like:</p>
      <blockquote>
        "Hi, this is [first name] from [city]. I just heard [record / artist /
        JG Radio], and [one honest reaction]. I'm plugged in to Joe Galaxy Radio."
      </blockquote>
      <ul>
        <li>Use first name only.</li>
        <li>No private info.</li>
        <li>No background music or copyrighted audio.</li>
        <li>Keep it clean enough for radio.</li>
        <li>Admin approval is required before your drop goes live.</li>
      </ul>

      {voiceDrop?.audioUrl ? (
        <audio controls preload="metadata" src={voiceDrop.audioUrl} />
      ) : null}

      {canRecordNew ? (
        <div className="hitlab-radio-voice-drop-recorder">
          <label>
            Optional script note
            <textarea
              value={listenerScript}
              maxLength="280"
              onChange={(event) => setListenerScript(event.target.value)}
              placeholder="One honest line you plan to say."
            />
          </label>
          <label className="hitlab-radio-voice-drop-consent">
            <input
              type="checkbox"
              checked={permissionGranted}
              onChange={(event) => setPermissionGranted(event.target.checked)}
            />
            I give JG Radio permission to review this voice drop and, if
            approved, add it to JG Radio live rotation.
          </label>
          {mediaBlobUrl ? (
            <div className="hitlab-radio-voice-drop-preview">
              <span>Play preview</span>
              <audio
                ref={previewAudioRef}
                controls
                preload="metadata"
                src={mediaBlobUrl}
                onLoadedMetadata={handleLoadedMetadata}
              />
              <span>
                Duration {formatSeconds(durationSeconds)} / {VOICE_DROP_MIN_SECONDS}-
                {VOICE_DROP_MAX_SECONDS}s
              </span>
            </div>
          ) : null}
          <div className="hitlab-radio-voice-drop-actions">
            {isRecording ? (
              <button
                type="button"
                className="hitlab-secondary-btn"
                onClick={stopVoiceDropRecording}
              >
                Stop recording
              </button>
            ) : (
              <button
                type="button"
                className="hitlab-secondary-btn"
                onClick={startVoiceDropRecording}
                disabled={busy}
              >
                {mediaBlobUrl ? 'Re-record' : 'Start recording'}
              </button>
            )}
            {mediaBlobUrl ? (
              <button
                type="button"
                className="hitlab-primary-btn"
                onClick={submitRecording}
                disabled={busy || !hasValidDuration || !permissionGranted}
              >
                {busy ? `Uploading ${uploadProgress}%` : 'Submit voice drop'}
              </button>
            ) : null}
          </div>
        </div>
      ) : (
        <p>
          {voiceDrop.status === 'approved'
            ? APPROVED_LIVE_LABEL
            : voiceDrop.status === 'rejected'
              ? 'Not approved'
              : 'Submitted for review'}
        </p>
      )}

      {loading ? <p role="status">Loading voice drop...</p> : null}
      {message ? <p className="hitlab-radio-voice-drop-message">{message}</p> : null}
      {error ? (
        <p className="hitlab-radio-feedback-pass-error" role="alert">
          {error}
        </p>
      ) : null}
    </section>
  );
}
